// Every Amazon building and where it stands on each step of the path to COD.
// Mirrors src/inrange_agents/amazon_overview.py (POST /amazon-overview/buildings).
import { useQuery } from "react-query";

import { QK } from "../queryKeys";
import type { RatioBasis } from "./useDashboard";
import { useAgentsHttp } from "../useAgentsHttp";

/** not_tracked: no record speaks to this step yet, so the page reads it as not happened
 * yet. It is kept apart from not_started because an awaited step with no record, after
 * steps that are done, is a blocker. */
export type StepState =
  | "done"
  | "open"
  | "blocked"
  | "not_started"
  | "not_tracked"
  | "not_applicable";

export interface BuildingStep {
  state: StepState;
  /** When it was done, where the record gives it. */
  date: string;
  /** When it is expected or must be done by, for an open step. */
  due: string;
  detail: string;
  /** The achievable kWp this step's outcome allows (structural). */
  kwp: number | null;
  /** The achievable kWac this step's outcome allows (grid offer). */
  kw_ac: number | null;
  /** ``due`` is our own estimate, not a date anyone gave us. */
  estimated: boolean;
  /** The proof behind ``date`` where it is a send or a recorded date: the email it
   * cites, by its subject, and who at InRange sent it; "" where none is on record. */
  source: string;
  sent_by: string;
  /** The day a wait on the other side began, where no document set when it ends: the
   * page counts the days since. Absent from an API deployed before it. */
  waiting_since?: string;
  /** The document the send behind ``date`` carried ("P02"). */
  delivers?: string;
  /** What the record found that sets ``kwp`` (the structural survey's finding). */
  finding?: string;
  /** A qualifier shown beside the state ("structure limits it, no figure yet"). */
  note?: string;
  /** Who the ask behind ``date`` went to ("Amazon"), and the day they last replied. */
  sent_to?: string;
  replied?: string;
  /** Where ``date`` comes from: an email logged as sent, a date an agent recorded, or an
   * operator's confirmation. */
  basis?: "email" | "recorded" | "operator" | "";
  /** The readiness area's decision ("accept_as_is", "remediate", "dont_proceed"). */
  outcome?: string;
  /** Structural sign-off: the feedback points still open and already closed on the
   * structural asks; null where the step counts none. */
  points_open?: number | null;
  points_closed?: number;
  /** The open points ``points_open`` counts; absent from an API deployed before them. */
  open_points?: OpenPoint[];
  /** How many items not received hold the step up (amazon_items.blocking). */
  blocking_items?: number;
  /** The state the step's own record gives before any item holds it up: what the page
   * falls back to when an override says the items have come. Absent from an API deployed
   * before it. */
  own_state?: StepState;
  /** Whose move the open step waits on, where the record says; absent or "" otherwise. */
  waits_on?: "us" | "them" | "";
  /** Grid offer: the application a budget estimate answered in place of an offer, and the
   * day the estimate came ("" where Grid did not date it); absent or "" where none stands. */
  estimate_for?: string;
  estimate_on?: string;
  /** Structural: the relied-on report's share of the evaluated roof it finds viable, and
   * the kWp that share is of the max-out design (null with no max-out size); null where no
   * report states one, absent from an API deployed before it. */
  roof_viable?: {
    min_pct: number;
    max_pct: number;
    after_works: boolean;
    kwp: { of: number; min: number; max: number } | null;
  } | null;
  /** The specialist whose record the step is read from, which a comment on it reaches; ""
   * for one read from the platform's status, absent from an API deployed before it or for
   * a viewer outside InRange. */
  agent?: string;
}

/** A feedback point still open on a structural ask (amazon_overview.OpenPoint). */
export interface OpenPoint {
  text: string;
  raised_by: string;
  raised_on: string;
  /** Who must act on it ("Amazon", "InRange"), "" where that is not on record. */
  owed_by: string;
  /** Its ``SUPPLIERS`` key ("client", "inrange"); absent from an API deployed before it. */
  owed_party?: string;
  /** The subject of the email that raised it, "" where not on record; absent from an API
   * deployed before it. */
  source?: string;
  /** Its own id, and the specialist whose ask it is on, which a comment on it reaches;
   * absent from an API deployed before them. */
  point_id?: string;
  agent?: string;
  /** The item on Amazon's list it asks for, which the page lists once in its place; ""
   * or absent where it asks for none. */
  need?: string;
}

/** Where a feedback point came from: who raised it, when, and the email's subject. */
export interface PointOrigin {
  raised_by: string;
  raised_on: string;
  source?: string;
}

/** Why a building needs an item: its clause on Amazon's list, a register clause that
 * states it, or an email or document that asks for it. */
export interface NeedSource {
  kind: "clause" | "register" | "request";
  label: string;
  upload_id?: string;
  quote?: string;
  line_id?: string;
  document_id?: string;
}

/** The latest ask for an item still to come. */
/** A viewer outside InRange gets no ids or agents (amazon_overview_external.py). */
export interface NeedAsk {
  action_id?: string;
  agent?: string;
  sent: string;
  /** The email the send is logged on, by its subject. */
  subject?: string;
  upload_id?: string;
  by: string;
  to: string;
  /** The first reply after it, "" while none is on record. */
  replied: string;
}

/** One thing we need from Amazon, or from another party, on a building
 * (src/inrange_agents/amazon_items.py). */
export interface AmazonNeed {
  key: string;
  /** The Step 1 step it is needed for. */
  step: string;
  text: string;
  clause: string;
  /** Where only some buildings need it, which: those with existing PV, with a landlord,
   * or whose roof has skylights; "" where every one does. */
  only_where: "" | "existing_pv" | "landlord" | "skylights";
  /** Every item is received, not received (asked for or not) or not applicable here
   * (Noaa, 19 Oct 2026); a graded roof item may also be partially received (20 Oct 2026). */
  status: "received" | "partial" | "missing" | "not_needed";
  /** Received from its grade, so it can be partially received; absent from an API
   * deployed before it. */
  graded?: boolean;
  /** Why a partially received item is partial (amazon_items._partial_view); null otherwise. */
  partial?: NeedPartial | null;
  /** Where it sits in its step's list; "" on a step whose list has none. */
  category?: string;
  /** The step whose date it is needed by, where not its own: the roof warranty items are
   * owed before the SPPA is signed (MSPPA s5.5(b)). "" or absent for its own. */
  due_step?: string;
  /** A date of its own it is needed by, and why, where no step dates it: Amazon's follow-up
   * with the landlord, due 14 days after the latest send or follow-up
   * (amazon_items.landlord_follow_up_view). Absent on every other item. */
  due?: string;
  due_why?: string;
  /** When it arrived, for a received one. */
  date: string;
  /** The citation showing it arrived, for a received one. */
  evidence: string;
  /** The email that citation names, by its subject; "" where none is on record. */
  source?: string;
  /** The document it came in, and the words that supply it. */
  source_id?: string;
  quote?: string;
  /** Who gave it, for a received one. */
  supplied_by?: string;
  /** One of the items the "Amazon design inputs" step counts: Amazon's, for a step that
   * waits on them. */
  design_input: boolean;
  /** On Amazon's list, rather than added for this building from an email or document. */
  listed?: boolean;
  /** The specialist keeping it, and who must give it, as the page names them ("Amazon",
   * "the landlord"). */
  keeper?: string;
  supplier?: string;
  /** The keeper's agent name, which a comment on the item reaches; absent from an API
   * deployed before it. */
  keeper_agent?: string;
  /** Set on an open feedback point listed as an item (needs.pointNeeds): its own id, and
   * where it came from. */
  point_id?: string;
  point_from?: PointOrigin;
  /** The open feedback points that ask for this item, where each came from; absent from
   * an API deployed before them. */
  points?: PointOrigin[];
  /** Owed by Amazon, whatever ``supplier`` names; absent from an API deployed before it. */
  from_amazon?: boolean;
  sources?: NeedSource[];
  /** For one still to come: its latest ask, null where none has been sent. */
  ask?: NeedAsk | null;
  /** Why it holds its step up while not received, whatever its status now, so an override
   * of the status blocks or frees the step by the backend's rule (needs.blocksItsStep). */
  blocking?: string[];
  /** What it waits on before it can be asked for or given. */
  waits_for?: string[];
}

/** Why a document held cannot be relied on as it stands (ready_checks.GAP_KINDS). */
export type GapKind =
  | "not_signed"
  | "not_dated"
  | "terms_missing"
  | "expired"
  | "other_address"
  | "part_of_roof"
  | "pages_missing"
  | "other";

export interface GradeGap {
  kind: GapKind;
  detail: string;
}

/** A partially received item's gaps, what we hold (by document name) and what is still
 * needed. A grade from before gaps has none. */
export interface NeedPartial {
  gaps: GradeGap[];
  held: { document: string; page: number | null }[];
  to_provide: string;
  graded_on: string;
}

/** An open action standing behind a step on one building. */
export interface BuildingAsk {
  action_id: string;
  /** The specialist whose list holds it. */
  agent: string;
  title: string;
  summary: string;
  /** Who it is with: name and organisation; "" for an internal task. */
  who: string;
  /** When it first went to them; "" while not sent. */
  sent: string;
  /** When their reply is due, for one sent and unanswered; else its own due date. */
  due: string;
  waiting_on_them: boolean;
  /** Their latest reply on record; "" for none. */
  replied: string;
  /** Its open feedback points, each with who raised it and when. */
  points: { text: string; raised_by: string; raised_on: string }[];
}

export interface AmazonBuilding {
  project_id: string;
  name: string;
  site_code: string;
  bma_status: string | null;
  /** "" where none is recorded. */
  committed_cod: string;
  /** The earliest COD the specialists' positions allow; "" where none is recorded. */
  achievable_cod: string;
  /** Who recorded the achievable COD, when, why and whose limits it accounts for; null
   * while unrecorded, absent from an API deployed before it. */
  achievable_cod_basis?: {
    by: string;
    set_at: string;
    reason: string;
    accounts_for: string;
  } | null;
  committed_kwp: number | null;
  committed_kw_ac: number | null;
  achievable_kwp: number | null;
  achievable_kw_ac: number | null;
  /** The DC/AC ratio the page reads a grid kWac against a structural kWp at, and where it
   * comes from (achievable_kwp.dc_ac_ratio); null where none is on record, absent from an
   * API deployed before it. */
  dc_ac_ratio?: { ratio: number; basis: RatioBasis } | null;
  /** Which achievable size units are written over on the page: set by the page
   * (overrides.applyOverrides), never by the API; absent where nothing is written over. */
  achievable_written?: { kwp: boolean; kwAc: boolean };
  steps: Record<string, BuildingStep>;
  /** Absent from an API deployed before the needs record. */
  needs?: AmazonNeed[];
  /** Each step's open asks; absent from an API deployed before them. */
  asks?: Record<string, BuildingAsk[]>;
  /** Amazon's follow-up with the landlord while the landlord's feedback on the preliminary
   * pack is awaited, listed only on Step 3's card for Landlord feedback and on the open
   * issues list; null otherwise, absent from an API deployed before it. */
  landlord_follow_up?: AmazonNeed | null;
  /** Amazon owns the building: no landlord, Amazon Real Estate approves instead. */
  freehold?: boolean;
  /** Grid's routes as its status row writes them (utils/optionRoutes.ts reads them);
   * absent from an API deployed before them. */
  grid_options?: string;
  /** The Schedule 1 deadline each step must meet, "" where none is recorded: the design
   * submission (design), landlord approval (llsignoff), installation start (install). */
  contract_dates?: { design: string; llsignoff: string; install: string };
  /** Design readiness's estimate of the detailed design's length, with who recorded it
   * and why; null while it has given none, absent from an API deployed before it. */
  design_days?: {
    days: number;
    by: string;
    set_at: string;
    reason: string;
    accounts_for: string;
  } | null;
}

/** England and Wales's bank holidays (working_days.public_holidays) in every year the
 * page's Business Day arithmetic reaches: the programme's years from today, and those of
 * every date the buildings and the values written over them hold. */
export interface BankHolidays {
  years: number[];
  dates: string[];
}

export interface AmazonOverview {
  buildings: AmazonBuilding[];
  bank_holidays: BankHolidays;
}

export const useAmazonOverview = () => {
  const http = useAgentsHttp();
  return useQuery<AmazonOverview>(
    [QK.amazonOverview],
    () => http<AmazonOverview>("/amazon-overview/buildings", {}),
    { refetchOnWindowFocus: false, staleTime: 60_000 },
  );
};
